/**
 * Объяснения подбора: коды → тексты интерфейса (BE-085, MATCHING §9).
 *
 * Backend хранит `code + params + evidence`, пользовательских строк не пишет ни
 * он, ни LLM. Здесь код превращается в ключ i18n, а числа — в форматы локали.
 * Чистый модуль без React: переводчик передаётся параметром и проверяется тестом.
 *
 * Два правила, которые нельзя нарушить:
 *
 * * **незнакомый код не показывается.** Вывести `PRIORITY_SCORE_HIGH` в
 *   интерфейс — это утечка внутренностей; отсутствие перевода означает, что
 *   вывод пропускается, а не печатается кодом;
 * * **вывода без основания не бывает.** Разбор `evidence` — не украшение, а
 *   обязательная часть: пользователь должен видеть, какой критерий, какое
 *   значение автомобиля и какой расчёт стоят за фразой (MVP_PRD §18).
 */

import { formatCents, formatKilometres, stateName } from '@entities/search-session';
import { formatSignedPercent } from '@entities/vehicle/analytics';
import { formatCalendarDate } from '@entities/vehicle/lib';

import type { EvidenceItem, ExplanationItem } from './types';

/** Переводчик и локаль. Отдельный тип, чтобы не тащить React в чистый модуль. */
export interface ExplanationContext {
  t: (key: string, params?: Record<string, unknown>) => string;
  /** Есть ли перевод. Незнакомый код пропускается, а не печатается как есть. */
  exists: (key: string) => boolean;
  locale: string;
  currency: string;
}

export const explanationKey = (code: string): string => `explanation.code.${code}`;
export const relaxationKey = (code: string): string => `explanation.relaxation.${code}`;
export const limitingRuleKey = (rule: string): string => `explanation.rule.${rule}`;
export const confidenceFactorKey = (code: string): string => `explanation.factor.${code}`;
export const conflictKey = (code: string): string => `explanation.conflict.${code}`;
export const severityKey = (severity: string): string => `explanation.severity.${severity}`;

const CENTS_SUFFIX = '_cents';
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Справочник, по которому переводится значение параметра.
 *
 * Один и тот же параметр (`preferred`, `actual`) в разных кодах означает разные
 * справочники, поэтому выбор идёт по коду вывода, а не по имени параметра.
 */
function dictionaryPrefix(code: string): string | null {
  if (code.includes('TRANSMISSION')) return 'catalog.transmission';
  if (code.includes('DRIVETRAIN')) return 'catalog.drivetrain';
  if (code.includes('FUEL')) return 'catalog.fuel';
  if (code.includes('BODY_TYPE')) return 'catalog.bodyType';
  return null;
}

const DICTIONARY_PARAMS = new Set([
  'preferred',
  'actual',
  'transmission',
  'drivetrain',
  'fuel_type',
  'body_type',
]);

function round(value: number, digits = 0): number {
  const factor = 10 ** digits;
  return Math.round(value * factor) / factor;
}

function formatNumber(value: number, locale: string, digits = 0): string {
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: 0,
    maximumFractionDigits: digits,
  }).format(value);
}

/**
 * Параметры вывода в форме, пригодной для интерполяции.
 *
 * Каждое число получает готовое представление рядом с исходным: `headroom_cents`
 * остаётся числом, а `headroom` — суммой в валюте локали. Так один ключ i18n
 * может использовать и то, и другое, и ни одна локаль не форматирует деньги сама.
 */
export function explanationParams(
  item: Pick<ExplanationItem, 'code' | 'params'>,
  context: ExplanationContext,
): Record<string, string | number> {
  const { t, locale, currency } = context;
  const result: Record<string, string | number> = {};
  const prefix = dictionaryPrefix(item.code);

  for (const [key, value] of Object.entries(item.params)) {
    if (typeof value === 'number') {
      result[key] = value;

      if (key.endsWith(CENTS_SUFFIX)) {
        // Знак несёт текст («выше бюджета», «ниже медианы»), поэтому сумма
        // показывается по модулю: «−1 600 € ниже» читалось бы как двойное отрицание.
        result[key.slice(0, -CENTS_SUFFIX.length)] = formatCents(Math.abs(value), locale, currency);
        continue;
      }
      if (key === 'delta_percent') {
        result[key] = formatSignedPercent(value, locale);
        continue;
      }
      if (key === 'ratio' || key === 'percentile' || key === 'headroom_ratio') {
        result[`${key === 'headroom_ratio' ? 'headroom' : key}_percent`] = round(value * 100);
        continue;
      }
      if (key === 'age_years') {
        result[key] = formatNumber(value, locale, 1);
        continue;
      }
      if (key === 'score' || key === 'data_confidence') {
        result[key] = round(value);
        continue;
      }
      if (key === 'mileage_km' || key === 'suggested_km') {
        result[key] = formatKilometres(value, locale);
        continue;
      }
      result[key] = formatNumber(value, locale);
      continue;
    }

    if (Array.isArray(value)) {
      // Списки кодов — оснащение или топливо: переводятся поштучно.
      const itemPrefix = prefix ?? 'vehicle.feature';
      result[key] = value
        .map((code) => (typeof code === 'string' ? t(`${itemPrefix}.${code}`) : String(code)))
        .join(', ');
      continue;
    }

    if (typeof value === 'string') {
      if (key === 'priority') {
        result[key] = t(`criteria.priority.${value}`);
        continue;
      }
      if (key === 'threshold') {
        result[key] = t(`explanation.threshold.${value}`);
        continue;
      }
      if (key === 'state_code' || key === 'home_state_code') {
        // Код земли — техническое значение: «NW» на экране ничего не говорит.
        result[key] = stateName(value);
        continue;
      }
      if (prefix !== null && DICTIONARY_PARAMS.has(key)) {
        result[key] = t(`${prefix}.${value}`);
        continue;
      }
      result[key] = value;
    }
  }

  return result;
}

/**
 * Текст вывода или `null`, если перевода нет.
 *
 * `null` означает «не показывать»: код без текста в интерфейсе — ошибка
 * локализации, а не контент.
 */
export function explanationText(
  item: Pick<ExplanationItem, 'code' | 'params'>,
  context: ExplanationContext,
): string | null {
  const key = explanationKey(item.code);
  if (!context.exists(key)) return null;
  return context.t(key, explanationParams(item, context));
}

/** Выводы, у которых есть текст **и** основание. Остальные не показываются. */
export function translatableExplanations(
  items: readonly ExplanationItem[],
  context: ExplanationContext,
): { item: ExplanationItem; text: string }[] {
  return items.flatMap((item) => {
    if (item.evidence.length === 0) return [];
    const text = explanationText(item, context);
    return text === null ? [] : [{ item, text }];
  });
}

// --- Основания (evidence) ---------------------------------------------------------------

export interface EvidenceLine {
  /** Что это за основание: критерий пользователя, поле объявления, рынок, расчёт. */
  kind: EvidenceItem['type'];
  label: string;
  value: string;
}

function formatValue(value: unknown, hint: string, context: ExplanationContext): string {
  const { t, locale, currency } = context;
  if (value === null || value === undefined) return t('catalog.noData');

  if (Array.isArray(value)) {
    if (value.length === 0) return t('catalog.noData');
    return value
      .map((code) => (typeof code === 'string' ? t(`vehicle.feature.${code}`) : String(code)))
      .join(', ');
  }

  if (typeof value === 'number') {
    if (hint.endsWith(CENTS_SUFFIX)) return formatCents(value, locale, currency);
    if (hint.includes('mileage')) {
      return t('catalog.spec.mileageValue', { value: formatKilometres(value, locale) });
    }
    return formatNumber(value, locale, 2);
  }

  if (typeof value === 'string') {
    if (ISO_DATE.test(value)) return formatCalendarDate(value, locale) ?? value;
    if (hint.endsWith('state_code')) return stateName(value);
    const dictionary = FIELD_DICTIONARY[hint];
    if (dictionary !== undefined && context.exists(`${dictionary}.${value}`)) {
      return t(`${dictionary}.${value}`);
    }
    return value;
  }

  if (typeof value === 'boolean') return t(value ? 'common.yes' : 'common.no');
  // Объект в основании означает расхождение контракта. Показывать
  // «[object Object]» нельзя — честнее сказать «нет данных».
  return t('catalog.noData');
}

/** Поля объявления, значения которых приходят кодами справочника. */
const FIELD_DICTIONARY: Record<string, string> = {
  fuel_type: 'catalog.fuel',
  transmission: 'catalog.transmission',
  drivetrain: 'catalog.drivetrain',
  body_type: 'catalog.bodyType',
};

/**
 * Основание в виде «что → какое значение».
 *
 * Путь критерия и имя поля переводятся; неизвестный путь показывается как есть —
 * он приходит из allowlist backend и остаётся техническим, но пропускать
 * основание нельзя: вывод без основания показывать запрещено.
 */
export function evidenceLine(item: EvidenceItem, context: ExplanationContext): EvidenceLine {
  const { t, locale } = context;

  switch (item.type) {
    case 'criterion':
      return {
        kind: item.type,
        label: t(`explanation.path.${item.path}`, { defaultValue: item.path }),
        value: formatValue(item.value, item.path, context),
      };
    case 'vehicle_field':
      return {
        kind: item.type,
        label: t(`explanation.field.${item.field}`, { defaultValue: item.field }),
        value: formatValue(item.value, item.field, context),
      };
    case 'benchmark': {
      const parts = [
        item.median_price_cents === null
          ? null
          : formatCents(item.median_price_cents, locale, context.currency),
        item.sample_size === null
          ? null
          : t('vehicle.market.sampleValue', { count: item.sample_size }),
        item.snapshot_date === null
          ? null
          : (formatCalendarDate(item.snapshot_date, locale) ?? item.snapshot_date),
      ].filter((part): part is string => part !== null);
      return {
        kind: item.type,
        label: t('explanation.evidence.benchmark'),
        value: parts.length > 0 ? parts.join(' · ') : t('catalog.noData'),
      };
    }
    case 'calculation':
      return {
        kind: item.type,
        label: t(`explanation.calculation.${item.code}`, { defaultValue: item.code }),
        value: context.exists(`explanation.calculationValue.${item.code}`)
          ? t(
              `explanation.calculationValue.${item.code}`,
              explanationParams({ code: item.code, params: item.params }, context),
            )
          : '',
      };
  }
}

// --- Предложения по смягчению (AC-09) ---------------------------------------------------

/**
 * Текст предложения или `null` без перевода.
 *
 * Предложение только показывается: применить его может исключительно
 * пользователь, вернувшись в чат (MATCHING §5.3).
 */
export function relaxationText(
  suggestion: { code: string; params: Record<string, unknown> },
  context: ExplanationContext,
): string | null {
  const key = relaxationKey(suggestion.code);
  if (!context.exists(key)) return null;
  return context.t(key, explanationParams(suggestion, context));
}
